import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  TrashIcon,
  IdentificationIcon,
  ClockIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SubjectForm } from '@/components/subjects/SubjectForm';
import {
  subjectsService,
  Subject,
  SubjectCaseLink,
  SubjectHistoryEntry,
  UpdateSubjectData,
} from '@/services/subjects.service';
import { getFullImageUrl } from '@/utils/imageUrl';

type TabId = 'overview' | 'cases' | 'history';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'cases', label: 'Linked Cases' },
  { id: 'history', label: 'History' },
];

const SubjectDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [subject, setSubject] = useState<Subject | null>(null);
  const [history, setHistory] = useState<SubjectHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchSubject = useCallback(async () => {
    if (!id) { return; }
    try {
      setLoading(true);
      setError(null);
      const [subjectData, historyData] = await Promise.all([
        subjectsService.getSubjectById(id),
        subjectsService.getSubjectHistory(id).catch(() => [] as SubjectHistoryEntry[]),
      ]);
      setSubject(subjectData);
      setHistory(historyData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subject');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSubject();
  }, [fetchSubject]);

  const handleSave = async (data: UpdateSubjectData, photoFile?: File) => {
    if (!id) { return; }
    setSubmitting(true);
    try {
      let updated = await subjectsService.updateSubject(id, data);
      if (photoFile) {
        const photoUrl = await subjectsService.uploadSubjectPhoto(id, photoFile);
        if (photoUrl) {
          updated = { ...updated, photoUrl };
        }
      }
      setSubject(updated);
      setEditing(false);
      const nextHistory = await subjectsService.getSubjectHistory(id).catch(() => history);
      setHistory(nextHistory);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !subject) { return; }
    // eslint-disable-next-line no-alert
    if (!confirm(`Delete subject ${subject.firstName} ${subject.lastName}? This cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    try {
      await subjectsService.deleteSubject(id);
      navigate('/security/subjects');
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err instanceof Error ? err.message : 'Failed to delete subject');
    } finally {
      setDeleting(false);
    }
  };

  const handleUnlink = async (link: SubjectCaseLink) => {
    if (!subject) { return; }
    // eslint-disable-next-line no-alert
    if (!confirm(`Unlink from this ${link.caseType.toLowerCase()}?`)) { return; }
    try {
      await subjectsService.unlinkSubjectFromCase(subject.id, link.caseType, link.caseId);
      await fetchSubject();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err instanceof Error ? err.message : 'Failed to unlink');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !subject) {
    return (
      <div className="p-6">
        <Button variant="outline" size="sm" onClick={() => navigate('/security/subjects')}>
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div className="mt-4 p-4 rounded-lg bg-danger-50 dark:bg-danger-900/20 text-danger-700 dark:text-danger-400">
          {error ?? 'Subject not found'}
        </div>
      </div>
    );
  }

  const roleConfig = subjectsService.getRoleTypeConfig(subject.roleType);
  const riskConfig = subjectsService.getRiskLevelConfig(subject.riskLevel);
  const displayName = subjectsService.getSubjectDisplayName(subject);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/security/subjects')}>
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
              {displayName}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded-full ${subjectsService.getBadgeClasses(roleConfig.color)}`}
              >
                {roleConfig.label}
              </span>
              {subject.riskLevel && (
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${subjectsService.getBadgeClasses(riskConfig.color)}`}
                >
                  {riskConfig.label} risk
                </span>
              )}
            </div>
          </div>
        </div>

        {!editing && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditing(true)}>
              <PencilSquareIcon className="w-4 h-4 mr-1" />
              Edit
            </Button>
            <Button variant="outline" onClick={handleDelete} disabled={deleting}>
              <TrashIcon className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </div>
        )}
      </div>

      {editing ? (
        <SubjectForm
          initialValues={{
            firstName: subject.firstName,
            lastName: subject.lastName,
            roleType: subject.roleType,
            title: subject.title ?? undefined,
            photoUrl: subject.photoUrl ?? undefined,
            email: subject.email ?? undefined,
            phone: subject.phone ?? undefined,
            addressLine1: subject.addressLine1 ?? undefined,
            addressLine2: subject.addressLine2 ?? undefined,
            city: subject.city ?? undefined,
            state: subject.state ?? undefined,
            postalCode: subject.postalCode ?? undefined,
            country: subject.country ?? undefined,
            riskLevel: subject.riskLevel ?? undefined,
            distanceFromOfficeMiles: subject.distanceFromOfficeMiles ?? undefined,
            vehicleMake: subject.vehicleMake ?? undefined,
            vehicleModel: subject.vehicleModel ?? undefined,
            vehicleColor: subject.vehicleColor ?? undefined,
            vehiclePlate: subject.vehiclePlate ?? undefined,
            socialMedia: subject.socialMedia ?? undefined,
            criminalHistory: subject.criminalHistory ?? undefined,
            supervisor: subject.supervisor ?? undefined,
            agency: subject.agency ?? undefined,
            jurisdiction: subject.jurisdiction ?? undefined,
            badgeNumber: subject.badgeNumber ?? undefined,
            notes: subject.notes ?? undefined,
          }}
          onSubmit={handleSave}
          onCancel={() => setEditing(false)}
          submitting={submitting}
          submitLabel="Save Changes"
        />
      ) : (
        <>
          <div className="border-b border-secondary-200 dark:border-secondary-700">
            <nav className="flex gap-6">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {activeTab === 'overview' && <OverviewTab subject={subject} />}
          {activeTab === 'cases' && (
            <CasesTab subject={subject} onUnlink={handleUnlink} />
          )}
          {activeTab === 'history' && <HistoryTab history={history} />}
        </>
      )}
    </div>
  );
};

const OverviewTab: React.FC<{ subject: Subject }> = ({ subject }) => {
  const rows: Array<[string, string | number | null | undefined]> = [
    ['Title', subject.title],
    ['Email', subject.email],
    ['Phone', subject.phone],
    ['Address', [subject.addressLine1, subject.addressLine2, subject.city, subject.state, subject.postalCode, subject.country]
      .filter(Boolean)
      .join(', ') || null],
    ['Distance from Office', subject.distanceFromOfficeMiles ? `${subject.distanceFromOfficeMiles} mi` : null],
    ['Vehicle', [subject.vehicleColor, subject.vehicleMake, subject.vehicleModel].filter(Boolean).join(' ') || null],
    ['Vehicle Plate', subject.vehiclePlate],
    ['Criminal History', subject.criminalHistory],
    ['Supervisor', subject.supervisor],
    ['Agency', subject.agency],
    ['Jurisdiction', subject.jurisdiction],
    ['Badge Number', subject.badgeNumber],
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Card className="md:col-span-1">
        <CardContent className="p-6 flex flex-col items-center text-center">
          <div className="w-32 h-32 rounded-full overflow-hidden bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center mb-4">
            {getFullImageUrl(subject.photoUrl) ? (
              <img src={getFullImageUrl(subject.photoUrl) ?? undefined} alt={subject.firstName} className="w-full h-full object-cover" />
            ) : (
              <IdentificationIcon className="w-16 h-16 text-secondary-400" />
            )}
          </div>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
            {subject.firstName} {subject.lastName}
          </h2>
          <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
            Added by {subject.createdByName}
          </p>
          <p className="text-xs text-secondary-400 mt-1">
            {new Date(subject.createdAt).toLocaleDateString()}
          </p>
        </CardContent>
      </Card>

      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {rows.map(([label, value]) =>
              value ? (
                <div key={label}>
                  <dt className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase">
                    {label}
                  </dt>
                  <dd className="text-sm text-secondary-900 dark:text-white mt-0.5">{value}</dd>
                </div>
              ) : null,
            )}
          </dl>
          {subject.socialMedia && subject.socialMedia.length > 0 && (
            <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
              <h4 className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase mb-2">
                Social Media
              </h4>
              <ul className="space-y-1">
                {subject.socialMedia.map((acc, idx) => (
                  <li key={idx} className="text-sm text-secondary-900 dark:text-white">
                    <span className="font-medium">{acc.platform}</span>
                    {acc.handle && <span>: {acc.handle}</span>}
                    {acc.url && (
                      <>
                        {' — '}
                        <a href={acc.url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                          {acc.url}
                        </a>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {subject.notes && (
            <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
              <h4 className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase mb-2">
                Notes
              </h4>
              <p className="text-sm text-secondary-900 dark:text-white whitespace-pre-wrap">{subject.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const CasesTab: React.FC<{ subject: Subject; onUnlink: (link: SubjectCaseLink) => void }> = ({
  subject,
  onUnlink,
}) => {
  const investigationLinks = (subject.investigationLinks ?? []).filter((l) => !l.unlinkedAt);
  const incidentLinks = (subject.incidentLinks ?? []).filter((l) => !l.unlinkedAt);

  const renderLink = (link: SubjectCaseLink) => (
    <div
      key={link.id}
      className="flex items-start justify-between gap-3 p-3 border border-secondary-200 dark:border-secondary-700 rounded-lg"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <LinkIcon className="w-4 h-4 text-secondary-400" />
          <span className="text-sm font-medium text-secondary-900 dark:text-white">
            {link.caseType === 'INVESTIGATION' ? 'Investigation' : 'Incident'} ·
          </span>
          <span className="font-mono text-sm">
            {link.caseNumber ?? '(deleted case)'}
          </span>
          {link.title && (
            <span className="text-secondary-700 dark:text-secondary-300">
              — {link.title}
            </span>
          )}
        </div>
        {link.caseRole && (
          <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">Role: {link.caseRole}</p>
        )}
        {link.relationshipToCase && (
          <p className="text-xs text-secondary-600 dark:text-secondary-400">Relationship: {link.relationshipToCase}</p>
        )}
        <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
          Linked by {link.linkedByName} on {new Date(link.linkedAt).toLocaleDateString()}
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={() => onUnlink(link)}>
        Unlink
      </Button>
    </div>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Linked Investigations ({investigationLinks.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {investigationLinks.length === 0 ? (
            <p className="text-sm text-secondary-500 dark:text-secondary-400">No linked investigations.</p>
          ) : (
            <div className="space-y-2">{investigationLinks.map(renderLink)}</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Linked Incidents ({incidentLinks.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {incidentLinks.length === 0 ? (
            <p className="text-sm text-secondary-500 dark:text-secondary-400">No linked incidents.</p>
          ) : (
            <div className="space-y-2">{incidentLinks.map(renderLink)}</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const HistoryTab: React.FC<{ history: SubjectHistoryEntry[] }> = ({ history }) => {
  if (history.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-sm text-secondary-500 dark:text-secondary-400">
          No audit entries yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Change History</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="space-y-4">
          {history.map((entry) => (
            <li key={entry.id} className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center">
                <ClockIcon className="w-4 h-4 text-secondary-500" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-secondary-900 dark:text-white">
                    {entry.action.replace(/_/g, ' ')}
                  </span>
                  <span className="text-secondary-500 dark:text-secondary-400">
                    by {entry.userName}
                  </span>
                </div>
                <p className="text-xs text-secondary-500 dark:text-secondary-400">
                  {new Date(entry.createdAt).toLocaleString()}
                </p>
                {entry.fieldChanges && Object.keys(entry.fieldChanges).length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-secondary-700 dark:text-secondary-300">
                    {Object.entries(entry.fieldChanges).map(([field, change]) => (
                      <li key={field} className="font-mono">
                        <span className="font-semibold">{field}</span>:{' '}
                        <span className="line-through text-danger-600 dark:text-danger-400">
                          {formatValue(change.from)}
                        </span>{' '}
                        →{' '}
                        <span className="text-success-600 dark:text-success-400">
                          {formatValue(change.to)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {entry.linkedCaseId && (
                  <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                    Case: {entry.linkedCaseType} {entry.linkedCaseId}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
};

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined || v === '') { return '—'; }
  if (typeof v === 'object') { return JSON.stringify(v); }
  if (typeof v === 'string') { return v; }
  if (typeof v === 'number' || typeof v === 'boolean') { return String(v); }
  return '—';
};

export default SubjectDetails;
